import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import { Button, Chip, Paper } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AddIcon from "@mui/icons-material/Add";
import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { getAuthToken } from "../../services/authStore";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { maybeCreateDriveFolder } from "../DriveFolderPrompt";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [takingOver, setTakingOver] = useState(false);
  const [contactPresence, setContactPresence] = useState({ isOnline: false, isTyping: false });

  const [contactTags, setContactTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);

  // isViewMode: só quando o ticket open pertence a outro agente
  const isViewMode = Boolean(
    ticket?.status === "open" &&
    ticket?.userId &&
    user?.id &&
    ticket.userId !== user.id
  );

  useEffect(() => {
    // Fechar a aba ou dar F5 não roda o cleanup do React abaixo — sem isso,
    // a inscrição de presença desse ticket fica ativa na sessão do WhatsApp
    // para sempre (não há TTL do lado do client). fetch com keepalive
    // sobrevive ao unload da página (sendBeacon não permite header de auth).
    const unsubscribeOnUnload = () => {
      fetch(`${getBackendUrl()}/messages/${ticketId}/unsubscribe-presence`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener("pagehide", unsubscribeOnUnload);

    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);

          if (data.whatsappId && data.status === "open" && !data.isGroup) {
            api.post(`/messages/${ticketId}/subscribe-presence`).catch(() => {});
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => {
      window.removeEventListener("pagehide", unsubscribeOnUnload);
      clearTimeout(delayDebounceFn);
      // Libera a inscrição de presença ao trocar/fechar o ticket — sem isso,
      // cada ticket aberto ao longo do dia acumulava mais uma inscrição na
      // página do WhatsApp Web pro resto da sessão, sem nunca liberar.
      api.post(`/messages/${ticketId}/unsubscribe-presence`).catch(() => {});
    };
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    const handleConnect = () => socket.emit("joinChatBox", `${ticketId}`);

    const handleTicket = (data) => {
      if (data.action === "update" && data.ticket?.id === +ticketId) {
        setTicket(data.ticket);
      }
      if (data.action === "delete" && data.ticketId === +ticketId) {
        toast.success("Ticket excluído com sucesso.");
        navigate("/tickets");
      }
    };

    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    const handlePresence = (data) => {
      setContact((prevContact) => {
        if (prevContact?.number && data.chatId?.startsWith(prevContact.number)) {
          setContactPresence({ isOnline: data.isOnline, isTyping: data.isTyping });
        }
        return prevContact;
      });
    };

    socket.emit("joinChatBox", `${ticketId}`);
    socket.on("connect", handleConnect);
    socket.on("ticket", handleTicket);
    socket.on("contact", handleContact);
    socket.on("presenceChanged", handlePresence);

    return () => {
      socket.emit("leaveChatBox", `${ticketId}`);
      socket.off("connect", handleConnect);
      socket.off("ticket", handleTicket);
      socket.off("contact", handleContact);
      socket.off("presenceChanged", handlePresence);
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleTakeOver = async () => {
    if (!user?.id) return;
    const ownerName = ticket?.user?.name;
    if (ownerName) {
      const confirmed = window.confirm(
        `Este atendimento está com ${ownerName}.\nDeseja assumir e transferir para você?`
      );
      if (!confirmed) return;
    }
    setTakingOver(true);
    try {
      await api.put(`/tickets/${ticketId}`, { userId: user.id, status: "open" });
      toast.success("Atendimento assumido");
      maybeCreateDriveFolder(contact);
      navigate(`/tickets/${ticketId}`);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.warning("Este ticket já foi assumido por outro agente.");
      } else {
        toastError(err);
      }
    } finally {
      setTakingOver(false);
    }
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const loadContactTags = useCallback(async (contactId) => {
    if (!contactId) return;
    try {
      const { data } = await api.get(`/contacts/${contactId}/tags`);
      setContactTags(Array.isArray(data) ? data : []);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    if (contact?.id) loadContactTags(contact.id);
  }, [contact?.id, loadContactTags]);

  useEffect(() => {
    api.get("/tags/all")
      .then(({ data }) => setAllTags(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleAddTag = async (tag) => {
    if (!contact?.id) return;
    setShowTagPicker(false);
    try {
      await api.post(`/contacts/${contact.id}/tags`, { name: tag.name, color: tag.color });
      await loadContactTags(contact.id);
    } catch (err) { toastError(err); }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      setContactTags(prev => prev.filter(t => t.id !== tagId));
    } catch (err) { toastError(err); }
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
              presence={contactPresence}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        {/* Barra de tags do contato */}
        {!loading && contact?.id && (
          <div style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 6,
            padding: "5px 12px",
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            minHeight: 36,
          }}>
            <LocalOfferIcon style={{ fontSize: 14, color: "#aaa", flexShrink: 0 }} />
            {contactTags.map(tag => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                onDelete={() => handleRemoveTag(tag.id)}
                style={{
                  backgroundColor: tag.color,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                }}
              />
            ))}
            <Chip
              icon={<AddIcon style={{ fontSize: 12 }} />}
              label="Tag"
              size="small"
              onClick={() => setShowTagPicker(v => !v)}
              style={{ height: 22, fontSize: 11, cursor: "pointer", border: "1px dashed #ccc", background: "transparent", color: "#888" }}
            />
          </div>
        )}
        {!loading && contact?.id && showTagPicker && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 12px", background: theme.palette.background.default, borderBottom: `1px solid ${theme.palette.divider}` }}>
            {allTags
              .filter(t => !contactTags.some(ct => ct.name === t.name))
              .map(tag => (
                <Chip
                  key={tag.name}
                  label={tag.name}
                  size="small"
                  onClick={() => handleAddTag(tag)}
                  style={{ backgroundColor: tag.color, color: "#fff", fontWeight: 600, fontSize: 11, cursor: "pointer", height: 24 }}
                />
              ))
            }
            {allTags.filter(t => !contactTags.some(ct => ct.name === t.name)).length === 0 && (
              <span style={{ fontSize: 12, color: "#999" }}>Todas as tags já aplicadas</span>
            )}
          </div>
        )}

        <ReplyMessageProvider>
          {(isViewMode && !loading) && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 16px",
              background: theme.palette.background.default,
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexWrap: "wrap",
              gap: 8,
            }}>
              <div style={{ display: "flex", alignItems: "center", color: "#666", fontSize: 14, flex: 1 }}>
                <VisibilityIcon style={{ marginRight: 8, fontSize: 18 }} />
                <span>
                  Atendimento em andamento{ticket?.user?.name ? <> com <strong>{ticket.user.name}</strong></> : null}
                </span>
              </div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleTakeOver}
                disabled={takingOver}
              >
                {takingOver ? "Assumindo..." : "Assumir Atendimento"}
              </Button>
            </div>
          )}
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
          ></MessagesList>
          {!isViewMode && ticket.status !== "pending" && (
            <MessageInput
              ticketStatus={ticket.status}
              contact={contact}
              whatsappId={ticket?.whatsappId}
            />
          )}
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        whatsappId={ticket?.whatsappId}
        isGroup={ticket?.isGroup}
        ticketId={ticketId}
      />
    </div>
  );
};

export default Ticket;
